import * as React from "react"
import { Head, Link } from "@inertiajs/react"
import { AppShell } from "@/components/AppShell"
import { Badge } from "@/components/ui/badge"
import { CompositionChart } from "@/components/ui/chart"
import {
  type DirectiveAnalytics,
  formatPercent,
  formatRp,
  formatRpCompact,
} from "@/lib/accountDirectives"

type PageProps = {
  analytics: DirectiveAnalytics
}

const DESCRIPTION =
  "Analitik deskriptif ledger peringatan produk: SKU berperingatan per akun, eksposur revenue berisiko, tingkat pemulihan, dan SKU pelanggar berulang."

export default function Analytics({ analytics }: PageProps) {
  const { by_account, revenue_at_risk, recovery, repeat_offenders } = analytics

  const totalWarningLines = by_account.reduce((sum, r) => sum + r.line_count, 0)

  const recoverySlices = [
    { label: "Peringatan aktif", value: recovery.warning },
    { label: "Pulih", value: recovery.cleared },
    { label: "Dihentikan", value: recovery.discontinued },
  ].filter((s) => s.value > 0)

  return (
    <>
      <Head title="Analitik Peringatan">
        <meta name="description" content={DESCRIPTION} />
        <meta property="og:title" content="Analitik Peringatan" />
        <meta property="og:description" content={DESCRIPTION} />
      </Head>
      <AppShell>
        <div className="mb-2 flex items-center gap-1.5 text-xs text-ink-muted">
          <span>Outlet</span>
          <span>/</span>
          <Link href="/admin/accounts" className="hover:text-ink-body">
            Account
          </Link>
          <span>/</span>
          <Link href="/admin/accounts/directives" className="hover:text-ink-body">
            Peringatan
          </Link>
          <span>/</span>
          <span className="text-ink-body font-medium">Analitik</span>
        </div>

        <div className="border-b border-hairline pb-6">
          <h1>Analitik Peringatan</h1>
          <p className="mt-1 text-sm text-ink-muted">{DESCRIPTION}</p>
        </div>

        {/* Summary stats */}
        <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
          <StatCard label="SKU berperingatan aktif" value={String(totalWarningLines)} />
          <StatCard label="Akun terdampak" value={String(by_account.length)} />
          <StatCard
            label="Revenue berisiko"
            value={formatRpCompact(revenue_at_risk.total)}
            tone="danger"
          />
          <StatCard
            label="Tingkat pemulihan"
            value={formatPercent(recovery.rate)}
            tone="success"
          />
        </div>

        {/* Warned SKUs by account */}
        <Section title="SKU berperingatan per akun" hint="Baris SKU berstatus peringatan pada peringatan yang masih aktif.">
          {by_account.length === 0 ? (
            <EmptyState message="Belum ada SKU berperingatan aktif." />
          ) : (
            <>
              <div className="hidden overflow-x-auto rounded-lg border border-hairline md:block">
                <table className="w-full text-sm">
                  <thead className="bg-surface text-left text-ink-muted">
                    <tr>
                      <th className="px-4 py-3 font-medium">Akun</th>
                      <th className="px-4 py-3 text-right font-medium">SKU peringatan</th>
                      <th className="px-4 py-3 text-right font-medium">Peringatan</th>
                    </tr>
                  </thead>
                  <tbody>
                    {by_account.map((r) => (
                      <tr key={r.account_id} className="border-t border-hairline hover:bg-surface">
                        <td className="px-4 py-3">
                          <Link href={`/admin/accounts/${r.account_id}`} className="font-medium">
                            {r.account_name ?? "—"}
                          </Link>
                        </td>
                        <td className="px-4 py-3 text-right tabular-nums text-ink-body">{r.line_count}</td>
                        <td className="px-4 py-3 text-right tabular-nums text-ink-muted">{r.directive_count}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="flex flex-col gap-3 md:hidden">
                {by_account.map((r) => (
                  <Link
                    key={r.account_id}
                    href={`/admin/accounts/${r.account_id}`}
                    className="rounded-lg border border-hairline bg-surface p-4"
                  >
                    <div className="font-medium text-ink-body">{r.account_name ?? "—"}</div>
                    <div className="mt-2 flex flex-wrap gap-3 text-sm text-ink-muted">
                      <span className="text-ink-body">{r.line_count} SKU peringatan</span>
                      <span>{r.directive_count} peringatan</span>
                    </div>
                  </Link>
                ))}
              </div>
            </>
          )}
        </Section>

        {/* Revenue at risk */}
        <Section
          title="Revenue berisiko"
          hint="Eksposur netto_wise periode terakhir untuk SKU peringatan aktif yang sudah dipetakan ke STAR (sumber: b2b_sell_out)."
        >
          {revenue_at_risk.by_account.length === 0 ? (
            <EmptyState message="Belum ada SKU peringatan aktif yang dipetakan dengan data sell-out." />
          ) : (
            <>
              <div className="hidden overflow-x-auto rounded-lg border border-hairline md:block">
                <table className="w-full text-sm">
                  <thead className="bg-surface text-left text-ink-muted">
                    <tr>
                      <th className="px-4 py-3 font-medium">Akun</th>
                      <th className="px-4 py-3 text-right font-medium">SKU</th>
                      <th className="px-4 py-3 text-right font-medium">Eksposur</th>
                    </tr>
                  </thead>
                  <tbody>
                    {revenue_at_risk.by_account.map((r) => (
                      <tr key={r.account_id} className="border-t border-hairline hover:bg-surface">
                        <td className="px-4 py-3">
                          <Link href={`/admin/accounts/${r.account_id}`} className="font-medium">
                            {r.account_name ?? "—"}
                          </Link>
                        </td>
                        <td className="px-4 py-3 text-right tabular-nums text-ink-muted">{r.sku_count}</td>
                        <td className="px-4 py-3 text-right tabular-nums text-ink-body">{formatRp(r.amount)}</td>
                      </tr>
                    ))}
                    <tr className="border-t border-hairline bg-surface">
                      <td className="px-4 py-3 font-medium text-ink-display">Total</td>
                      <td className="px-4 py-3" />
                      <td className="px-4 py-3 text-right font-medium tabular-nums text-danger">
                        {formatRp(revenue_at_risk.total)}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div className="flex flex-col gap-3 md:hidden">
                {revenue_at_risk.by_account.map((r) => (
                  <Link
                    key={r.account_id}
                    href={`/admin/accounts/${r.account_id}`}
                    className="flex items-center justify-between gap-2 rounded-lg border border-hairline bg-surface p-4"
                  >
                    <div>
                      <div className="font-medium text-ink-body">{r.account_name ?? "—"}</div>
                      <div className="text-xs text-ink-muted">{r.sku_count} SKU</div>
                    </div>
                    <div className="tabular-nums font-medium text-ink-body">{formatRp(r.amount)}</div>
                  </Link>
                ))}
                <div className="flex items-center justify-between rounded-lg border border-hairline bg-surface p-4">
                  <span className="font-medium text-ink-display">Total</span>
                  <span className="tabular-nums font-medium text-danger">{formatRp(revenue_at_risk.total)}</span>
                </div>
              </div>
            </>
          )}
        </Section>

        {/* Recovery composition */}
        <Section
          title="Tingkat pemulihan"
          hint="Bagian baris SKU yang sudah selesai (pulih vs dihentikan), beserta yang masih dalam peringatan."
        >
          <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_auto]">
            <CompositionChart
              data={recoverySlices}
              emptyMessage="Belum ada baris SKU untuk ditampilkan."
            />
            <div className="flex flex-col justify-center gap-2 text-sm">
              <Metric label="Pulih (cleared)" value={recovery.cleared} tone="success" />
              <Metric label="Dihentikan" value={recovery.discontinued} tone="muted" />
              <Metric label="Masih peringatan" value={recovery.warning} tone="danger" />
              <div className="mt-2 border-t border-hairline pt-2">
                <div className="text-xs text-ink-muted">
                  Pemulihan = pulih / (pulih + dihentikan) dari {recovery.resolved} baris terselesaikan
                </div>
                <div className="mt-1 text-2xl font-semibold tabular-nums text-ink-display">
                  {formatPercent(recovery.rate)}
                </div>
              </div>
            </div>
          </div>
        </Section>

        {/* Repeat offenders */}
        <Section
          title="SKU pelanggar berulang"
          hint="product_code yang berperingatan di ≥2 peringatan berbeda, dengan sebaran akun dan periode."
        >
          {repeat_offenders.length === 0 ? (
            <EmptyState message="Belum ada SKU yang berperingatan di lebih dari satu peringatan." />
          ) : (
            <>
              <div className="hidden overflow-x-auto rounded-lg border border-hairline md:block">
                <table className="w-full text-sm">
                  <thead className="bg-surface text-left text-ink-muted">
                    <tr>
                      <th className="px-4 py-3 font-medium">SKU</th>
                      <th className="px-4 py-3 text-right font-medium">Peringatan</th>
                      <th className="px-4 py-3 text-right font-medium">Akun</th>
                      <th className="px-4 py-3 text-right font-medium">Periode</th>
                    </tr>
                  </thead>
                  <tbody>
                    {repeat_offenders.map((r) => (
                      <tr key={r.product_code} className="border-t border-hairline hover:bg-surface">
                        <td className="px-4 py-3">
                          <div className="font-medium text-ink-body">{r.product_code}</div>
                          {r.sample_description && (
                            <div className="text-xs text-ink-muted">{r.sample_description}</div>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <Badge tone="danger">{r.directive_count}</Badge>
                        </td>
                        <td className="px-4 py-3 text-right tabular-nums text-ink-body">{r.account_count}</td>
                        <td className="px-4 py-3 text-right tabular-nums text-ink-muted">{r.period_count}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="flex flex-col gap-3 md:hidden">
                {repeat_offenders.map((r) => (
                  <div key={r.product_code} className="rounded-lg border border-hairline bg-surface p-4">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <div className="font-medium text-ink-body">{r.product_code}</div>
                        {r.sample_description && (
                          <div className="text-xs text-ink-muted">{r.sample_description}</div>
                        )}
                      </div>
                      <Badge tone="danger">{r.directive_count} peringatan</Badge>
                    </div>
                    <div className="mt-2 flex flex-wrap gap-3 text-sm text-ink-muted">
                      <span>{r.account_count} akun</span>
                      <span>{r.period_count} periode</span>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </Section>
      </AppShell>
    </>
  )
}

function StatCard({
  label,
  value,
  tone,
}: {
  label: string
  value: string
  tone?: "danger" | "success"
}) {
  const valueColor =
    tone === "danger"
      ? "text-danger"
      : tone === "success"
        ? "text-green-600 dark:text-green-400"
        : "text-ink-body"
  return (
    <div className="rounded-lg border border-hairline bg-surface p-4">
      <div className={`text-2xl font-semibold tabular-nums ${valueColor}`}>{value}</div>
      <div className="mt-1 text-xs text-ink-muted">{label}</div>
    </div>
  )
}

function Section({
  title,
  hint,
  children,
}: {
  title: string
  hint?: string
  children: React.ReactNode
}) {
  return (
    <section className="mt-10">
      <h2>{title}</h2>
      {hint && <p className="mt-1 text-sm text-ink-muted">{hint}</p>}
      <div className="mt-4">{children}</div>
    </section>
  )
}

function EmptyState({ message }: { message: string }) {
  return (
    <div className="rounded-lg border border-dashed border-hairline bg-surface p-8 text-center text-sm text-ink-muted">
      {message}
    </div>
  )
}

function Metric({
  label,
  value,
  tone,
}: {
  label: string
  value: number
  tone: "danger" | "success" | "muted"
}) {
  const dot =
    tone === "danger" ? "bg-danger" : tone === "success" ? "bg-green-500" : "bg-ink-muted"
  return (
    <div className="flex items-center gap-2">
      <span className={`inline-block h-2.5 w-2.5 rounded-sm ${dot}`} aria-hidden="true" />
      <span className="flex-1 text-ink-body">{label}</span>
      <span className="tabular-nums font-medium text-ink-display">{value}</span>
    </div>
  )
}
